import { FormEvent, useState } from 'react';
import { CalendarClock, MapPin, Pause, Play } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@tark/shared/components';
import {
  useSaveScheduleBookableOffering,
  useScheduleBookableOfferings,
  useSchedulePrograms,
} from '@tark/shared/hooks';
import type { TenantBookableOffering } from '@tark/shared/types';

export function CoachBookableOfferingsPanel({ enabled }: { enabled: boolean }) {
  const query = useScheduleBookableOfferings(enabled);
  const programs = useSchedulePrograms(enabled);
  const save = useSaveScheduleBookableOffering();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TenantBookableOffering | null>(null);
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(45);
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [programId, setProgramId] = useState<number | null>(null);
  const [paymentPolicy, setPaymentPolicy] = useState<'OFF_PLATFORM' | 'STRIPE'>('OFF_PLATFORM');

  const reset = () => {
    setEditing(null);
    setShowForm(false);
    setTitle('');
    setDuration(45);
    setLocation('');
    setPrice('');
    setError(null);
    setProgramId(programs.data?.[0]?.programId ?? null);
    setPaymentPolicy('OFF_PLATFORM');
  };

  const beginEdit = (item: TenantBookableOffering) => {
    setEditing(item);
    setShowForm(true);
    setTitle(item.title);
    setDuration(item.durationMinutes);
    setLocation(item.locationLabel ?? '');
    setPrice(item.priceAmount == null ? '' : String(item.priceAmount));
    setProgramId(item.programId ?? null);
    setPaymentPolicy('OFF_PLATFORM');
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (paymentPolicy !== 'OFF_PLATFORM') {
      setError('Choose Pay outside TARK before saving this lesson.');
      return;
    }
    const amount = price.trim() ? Number(price) : null;
    if (!title.trim() || !programId || amount == null || !Number.isFinite(amount) || amount <= 0) {
      setError('Choose a class, add a lesson name, and enter a price.');
      return;
    }
    try {
      setError(null);
      await save.mutateAsync({
        offeringId: editing?.offeringId,
        payload: {
          title: title.trim(),
          deliveryMode: 'IN_PERSON',
          programId,
          durationMinutes: duration,
          slotStepMinutes: 15,
          locationLabel: location.trim() || null,
          priceAmount: amount,
          currencyCode: 'USD',
          bookingHorizonDays: editing?.bookingHorizonDays ?? 30,
          minimumNoticeMinutes: editing?.minimumNoticeMinutes ?? 720,
          capacity: editing?.capacity ?? 1,
          paymentPolicy,
          active: editing?.active ?? true,
          version: editing?.version,
        },
      });
      reset();
    } catch {
      setError('This lesson could not be saved. Refresh and try again.');
    }
  };

  const toggle = async (item: TenantBookableOffering) => {
    if (item.paymentPolicy !== 'OFF_PLATFORM') {
      setError('Edit this older lesson to use Pay outside TARK before changing its availability.');
      return;
    }
    await save.mutateAsync({
      offeringId: item.offeringId,
      payload: {
        title: item.title,
        deliveryMode: 'IN_PERSON',
        programId: item.programId,
        durationMinutes: item.durationMinutes,
        slotStepMinutes: item.slotStepMinutes,
        locationLabel: item.locationLabel,
        priceAmount: item.priceAmount,
        currencyCode: item.currencyCode,
        bookingHorizonDays: item.bookingHorizonDays,
        minimumNoticeMinutes: item.minimumNoticeMinutes,
        capacity: item.capacity,
        paymentPolicy: 'OFF_PLATFORM',
        active: !item.active,
        version: item.version,
      },
    });
  };

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-primary" />
          Family booking
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {query.isLoading ? <p className="text-sm text-text-muted">Loading bookable lessons...</p> : null}
          {query.isError ? <p className="text-sm text-red-600">Bookable lessons could not load.</p> : null}
          {query.data?.map((item) => (
            <div
              key={item.offeringId}
              className="flex min-h-14 w-full items-center gap-3 rounded-lg border border-border bg-surface-elevated/30 px-4 py-3 text-left"
            >
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => beginEdit(item)} aria-label={`Edit ${item.title}`}>
                <p className="font-semibold text-text">{item.title}</p>
                <p className="mt-1 text-xs text-text-muted">
                  {item.durationMinutes} min
                  {item.locationLabel ? ` · ${item.locationLabel}` : ''}
                  {item.priceAmount == null ? '' : ` · $${Number(item.priceAmount).toFixed(2)}`}
                  {item.active ? '' : ' · Paused'}
                  {item.paymentPolicy === 'OFF_PLATFORM' ? '' : ' · Not bookable until payment is changed'}
                </p>
              </button>
              <Button
                size="sm"
                variant="outline"
                aria-label={item.active ? 'Pause lesson' : 'Resume lesson'}
                disabled={item.paymentPolicy !== 'OFF_PLATFORM'}
                onClick={(event) => {
                  event.stopPropagation();
                  void toggle(item);
                }}
              >
                {item.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
            </div>
          ))}
          {!query.isLoading && !query.isError && (query.data?.length ?? 0) === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-4 text-sm text-text-muted">
              Add one lesson families can book during your open times.
            </p>
          ) : null}
        </div>
        <Button type="button" variant="outline" onClick={() => setShowForm(!showForm)} aria-expanded={showForm}>{showForm ? 'Hide lesson form' : 'Add lesson'}</Button>
        <form hidden={!showForm} className="space-y-3 rounded-lg border border-border bg-surface-elevated/20 p-4" onSubmit={submit}>
          <p className="font-semibold text-text">{editing ? 'Edit lesson' : 'Add bookable lesson'}</p>
          <p className="inline-flex min-h-9 items-center gap-1.5 rounded-lg bg-primary/10 px-3 text-xs font-semibold text-primary">
            <MapPin className="h-4 w-4" /> In-person lesson
          </p>
          <Input label="Lesson name" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Private lesson" />
          <label className="text-sm font-medium text-text">
            Class
            <select
              className="mt-1 h-11 w-full rounded-lg border border-border bg-surface px-3"
              value={programId ?? ''}
              onChange={(event) => setProgramId(event.target.value ? Number(event.target.value) : null)}
            >
              <option value="">Choose a class</option>
              {(programs.data ?? []).map((program) => <option key={program.programId} value={program.programId}>{program.name}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-medium text-text">
              Duration
              <select className="mt-1 h-11 w-full rounded-lg border border-border bg-surface px-3" value={duration} onChange={(event) => setDuration(Number(event.target.value))}>
                {[30, 45, 60, 90].map((value) => <option key={value} value={value}>{value} min</option>)}
              </select>
            </label>
            <Input label="Price" type="number" min="0" step="0.01" value={price} onChange={(event) => setPrice(event.target.value)} placeholder="Required" />
          </div>
          <Input label="Court or venue" value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Optional" />
          <label className="text-sm font-medium text-text">
            Payment
            <select
              className="mt-1 h-11 w-full rounded-lg border border-border bg-surface px-3"
              value={paymentPolicy}
              onChange={(event) => setPaymentPolicy(event.target.value as 'OFF_PLATFORM' | 'STRIPE')}
            >
              <option value="OFF_PLATFORM">Pay outside TARK</option>
            </select>
          </label>
          <p className="text-xs text-text-muted">Record payments received outside TARK.</p>
          {editing && editing.paymentPolicy !== 'OFF_PLATFORM' ? (
            <p className="text-xs text-text-muted">This older lesson uses card checkout. Choose Pay outside TARK before saving changes.</p>
          ) : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          <div className="flex gap-2">
            <Button type="submit" isLoading={save.isPending}>{editing ? 'Save changes' : 'Add lesson'}</Button>
            {editing ? <Button type="button" variant="outline" onClick={reset}>Cancel</Button> : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
